import { Component, Input, OnChanges, SimpleChanges, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';

import { PmAdvancedApiService } from './pm-advanced.service';
import { PmReportsApiService } from '../pm-reports/pm-reports.service';
import {
  TIMELINE_ZOOM_OPTIONS,
  TimelineBarDto,
  TimelineDependencyDto,
  TimelineItemDto,
  TimelineZoom
} from './pm-advanced.models';

export interface TimelineDependencyRow {
  fromTitle: string;
  toTitle: string;
  fromProject?: string;
  toProject?: string;
  type?: string;
}

@Component({
  selector: 'app-pm-timeline-view',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatButtonModule, MatIconModule, MatSelectModule, MatFormFieldModule,
    MatInputModule, MatProgressSpinnerModule, MatSnackBarModule, MatTooltipModule,
    MatChipsModule
  ],
  templateUrl: './pm-timeline-view.component.html',
  styleUrl: './pm-timeline-view.component.scss'
})
export class PmTimelineViewComponent implements OnChanges {
  private readonly pm = inject(PmAdvancedApiService);
  private readonly reports = inject(PmReportsApiService);
  private readonly snack = inject(MatSnackBar);

  @Input() projectId = '';
  @Input() portfolioId = '';
  @Input() showShare = true;

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<TimelineItemDto[]>([]);
  readonly dependencies = signal<TimelineDependencyDto[]>([]);
  readonly criticalPath = signal<Set<string>>(new Set());
  readonly rangeStart = signal<Date | null>(null);
  readonly rangeEnd = signal<Date | null>(null);
  readonly shareLink = signal<string | null>(null);
  readonly sharing = signal(false);
  readonly exporting = signal(false);
  readonly portfolioName = signal<string | null>(null);

  readonly isMultiProject = computed(() => {
    const names = new Set(this.items().map(i => i.projectName).filter(Boolean));
    return names.size > 1;
  });

  zoom: TimelineZoom = 'Month';
  readonly zoomOptions = TIMELINE_ZOOM_OPTIONS;

  editingItem: TimelineItemDto | null = null;
  editStartDate = '';
  editEndDate = '';

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['projectId'] || changes['portfolioId']) && (this.projectId || this.portfolioId)) {
      this.loadTimeline();
    }
  }

  onZoomChange(): void {
    this.loadTimeline();
  }

  loadTimeline(): void {
    if (this.portfolioId) {
      this.loadPortfolioTimeline();
    } else if (this.projectId) {
      this.loadProjectTimeline();
    }
  }

  private loadProjectTimeline(): void {
    this.loading.set(true);
    this.error.set(null);
    this.portfolioName.set(null);
    this.pm.getTimeline(this.projectId, this.zoom).subscribe({
      next: (data) => {
        const bars = Array.isArray(data?.bars) ? data.bars : [];
        const mapped = bars.length
          ? this.mapBarsToItems(bars)
          : (Array.isArray(data?.items) ? data.items : []);
        this.applyTimelineData(
          mapped,
          data?.dependencies,
          data?.from ?? data?.rangeStart,
          data?.to ?? data?.rangeEnd,
          data?.criticalPath
        );
      },
      error: () => {
        this.items.set([]);
        this.dependencies.set([]);
        this.criticalPath.set(new Set());
        this.rangeStart.set(null);
        this.rangeEnd.set(null);
        this.error.set('Timeline data is not available yet.');
        this.loading.set(false);
      }
    });
  }

  private loadPortfolioTimeline(): void {
    this.loading.set(true);
    this.error.set(null);
    this.pm.getPortfolioTimeline(this.portfolioId, this.zoom).subscribe({
      next: (data) => {
        this.portfolioName.set(data?.accountName ?? null);
        const bars = Array.isArray(data?.bars) ? data.bars : [];
        const items = this.mapBarsToItems(bars.map(b => ({
          id: b.id,
          itemType: b.itemType,
          title: b.title,
          subtitle: b.subtitle,
          startDate: b.startDate,
          endDate: b.endDate,
          status: b.status
        })), bars.map(b => ({ id: b.id, projectId: b.projectId, projectName: b.projectName })));
        this.applyTimelineData(items, data?.dependencies, data?.from, data?.to, data?.criticalPath);
      },
      error: () => {
        this.items.set([]);
        this.dependencies.set([]);
        this.criticalPath.set(new Set());
        this.rangeStart.set(null);
        this.rangeEnd.set(null);
        this.error.set('Portfolio timeline is not available yet.');
        this.loading.set(false);
      }
    });
  }

  private mapBarsToItems(
    bars: TimelineBarDto[],
    projectMeta?: { id: string; projectId?: string; projectName?: string }[]
  ): TimelineItemDto[] {
    const metaById = new Map((projectMeta ?? []).map(m => [m.id, m]));
    return bars
      .filter(b => b.startDate || b.endDate)
      .map(b => {
        const meta = metaById.get(b.id);
        const start = b.startDate || b.endDate!;
        const end = b.endDate || b.startDate!;
        return {
          id: b.id,
          lane: this.laneForItemType(b.itemType),
          title: b.title,
          startDate: start,
          endDate: end,
          projectId: meta?.projectId,
          projectName: meta?.projectName,
          itemType: b.itemType,
          status: b.status,
          color: this.itemTypeColor(b.itemType),
          key: b.subtitle ? undefined : b.title
        } as TimelineItemDto;
      });
  }

  private laneForItemType(itemType?: string): string {
    const t = (itemType || '').toLowerCase();
    if (t.includes('initiative')) return 'Initiatives';
    if (t.includes('epic')) return 'Epics';
    if (t.includes('story')) return 'Stories';
    if (t.includes('task')) return 'Tasks';
    if (t.includes('sprint')) return 'Sprints';
    if (t.includes('version')) return 'Versions';
    if (t.includes('milestone')) return 'Milestones';
    return itemType || 'Other';
  }

  private applyTimelineData(
    rawItems: TimelineItemDto[] | undefined,
    rawDeps: TimelineDependencyDto[] | undefined,
    rangeStart?: string,
    rangeEnd?: string,
    criticalPathIds?: string[]
  ): void {
    const list = Array.isArray(rawItems) ? rawItems : [];
    this.items.set(list);
    this.dependencies.set(this.normalizeDependencies(rawDeps));
    this.criticalPath.set(new Set((criticalPathIds ?? []).map(String)));
    const start = rangeStart ? new Date(rangeStart) : this.inferRangeStart(list);
    const end = rangeEnd ? new Date(rangeEnd) : this.inferRangeEnd(list);
    this.rangeStart.set(start);
    this.rangeEnd.set(end);
    this.loading.set(false);
  }

  private normalizeDependencies(rawDeps: TimelineDependencyDto[] | undefined): TimelineDependencyDto[] {
    if (!Array.isArray(rawDeps)) return [];
    return rawDeps.map(d => ({
      fromItemId: d.fromItemId || d.fromId,
      toItemId: d.toItemId || d.toId,
      type: d.type || d.linkType,
      fromId: d.fromId,
      toId: d.toId,
      linkType: d.linkType
    }));
  }

  isCritical(item: TimelineItemDto): boolean {
    return this.criticalPath().has(item.id);
  }

  lanes(): string[] {
    if (this.isMultiProject()) {
      const projects = [...new Set(this.items().map(i => i.projectName || 'Unknown'))];
      return projects.sort();
    }
    const order = ['Epics', 'Initiatives', 'Stories', 'Tasks', 'Sprints', 'Versions', 'Milestones'];
    const found = [...new Set(this.items().map(i => i.lane || 'Other'))];
    return [...order.filter(l => found.includes(l)), ...found.filter(l => !order.includes(l))];
  }

  laneItems(lane: string): TimelineItemDto[] {
    if (this.isMultiProject()) {
      return this.items().filter(i => (i.projectName || 'Unknown') === lane);
    }
    return this.items().filter(i => (i.lane || 'Other') === lane);
  }

  barTooltip(item: TimelineItemDto): string {
    const parts = [item.title];
    if (item.projectName) parts.unshift(item.projectName);
    if (item.itemType) parts.push(`(${item.itemType})`);
    parts.push(`${item.startDate} – ${item.endDate}`);
    if (item.status) parts.push(`· ${item.status}`);
    if (this.isCritical(item)) parts.push('· critical path');
    return parts.join(' ');
  }

  barStyle(item: TimelineItemDto): Record<string, string> {
    const start = this.rangeStart();
    const end = this.rangeEnd();
    if (!start || !end) {
      return { left: '0%', width: '20%' };
    }
    const totalMs = Math.max(end.getTime() - start.getTime(), 1);
    const itemStart = new Date(item.startDate).getTime();
    const itemEnd = new Date(item.endDate).getTime();
    const left = ((itemStart - start.getTime()) / totalMs) * 100;
    const width = Math.max(((itemEnd - itemStart) / totalMs) * 100, 2);
    return {
      left: `${Math.min(Math.max(left, 0), 98)}%`,
      width: `${Math.min(width, 100 - left)}%`,
      background: item.color || this.laneColor(item.lane, item.itemType)
    };
  }

  laneColor(lane: string, itemType?: string): string {
    const type = (itemType || lane).toLowerCase();
    if (type.includes('initiative')) return '#7c3aed';
    if (type.includes('epic')) return '#6366f1';
    if (type.includes('story')) return '#0891b2';
    if (type.includes('task')) return '#64748b';
    if (type.includes('sprint')) return '#0f766e';
    if (type.includes('version')) return '#2563eb';
    if (type.includes('milestone')) return '#b45309';
    switch (lane) {
      case 'Epics': return '#6366f1';
      case 'Initiatives': return '#7c3aed';
      case 'Stories': return '#0891b2';
      case 'Tasks': return '#64748b';
      case 'Sprints': return '#0f766e';
      case 'Versions': return '#2563eb';
      case 'Milestones': return '#b45309';
      default: return '#64748b';
    }
  }

  itemTypeColor(itemType?: string): string {
    return this.laneColor(itemType || '', itemType);
  }

  openEdit(item: TimelineItemDto): void {
    if (this.portfolioId) return;
    this.editingItem = item;
    this.editStartDate = item.startDate?.slice(0, 10) ?? '';
    this.editEndDate = item.endDate?.slice(0, 10) ?? '';
  }

  cancelEdit(): void {
    this.editingItem = null;
  }

  saveEdit(): void {
    if (!this.editingItem || !this.projectId) return;
    this.pm.updateTimelineItem(this.editingItem.id, {
      startDate: this.editStartDate || undefined,
      endDate: this.editEndDate || undefined,
      dueDate: this.editEndDate || undefined
    }).subscribe({
      next: () => {
        this.editingItem = null;
        this.loadTimeline();
      },
      error: () => this.snack.open('Could not update timeline item.', 'Dismiss', { duration: 4000 })
    });
  }

  shareRoadmap(): void {
    if (!this.projectId || this.sharing()) return;
    this.sharing.set(true);
    this.pm.shareRoadmap(this.projectId).subscribe({
      next: (res) => {
        const token = res?.token;
        const path = res?.shareUrl || (token ? `/roadmap/shared/${token}` : null);
        if (path) {
          const full = path.startsWith('http') ? path : `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
          this.shareLink.set(full);
          navigator.clipboard?.writeText(full).catch(() => undefined);
          this.snack.open('Share link copied to clipboard.', 'Dismiss', { duration: 4000 });
        }
        this.sharing.set(false);
      },
      error: () => {
        this.snack.open('Share endpoint not available yet.', 'Dismiss', { duration: 4000 });
        this.sharing.set(false);
      }
    });
  }

  exportRoadmap(): void {
    if (!this.projectId || this.exporting()) return;
    this.exporting.set(true);
    this.reports.exportReport(this.projectId, 'roadmap', 'csv').subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `roadmap-${this.projectId}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        this.exporting.set(false);
        this.snack.open('Roadmap exported.', 'Dismiss', { duration: 3000 });
      },
      error: () => {
        this.exporting.set(false);
        this.snack.open('Roadmap export failed.', 'Dismiss', { duration: 4000 });
      }
    });
  }

  dependencyRows(): TimelineDependencyRow[] {
    const itemMap = new Map(this.items().map(i => [i.id, i]));
    return this.dependencies().map(d => {
      const fromId = d.fromItemId || d.fromId || '';
      const toId = d.toItemId || d.toId || '';
      const from = itemMap.get(fromId);
      const to = itemMap.get(toId);
      return {
        fromTitle: from?.title || fromId,
        toTitle: to?.title || toId,
        fromProject: from?.projectName,
        toProject: to?.projectName,
        type: d.type || d.linkType
      };
    });
  }

  private inferRangeStart(items: TimelineItemDto[]): Date {
    if (!items.length) return new Date();
    const min = Math.min(...items.map(i => new Date(i.startDate).getTime()));
    return new Date(min);
  }

  private inferRangeEnd(items: TimelineItemDto[]): Date {
    if (!items.length) {
      const d = new Date();
      d.setMonth(d.getMonth() + 3);
      return d;
    }
    const max = Math.max(...items.map(i => new Date(i.endDate).getTime()));
    return new Date(max);
  }
}
